import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { SupplyModuleConfig } from '@/types/moduleConfig';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import WestIcon from '@mui/icons-material/West';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  PURCHASE_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  WBS_CC_OPTIONS,
} from '@/constants/orderConstants';

const CATALOG_ROWS_PER_PAGE = 25;

const PR_DRAFT_CART_KEY = 'equip:pr-draft-cart';
const PR_DRAFT_FORM_KEY = 'equip:pr-draft-form';

/** Persist PR draft to localStorage. */
function savePrDraft(
  cart: Record<string, { quantity: number; actualPrice: number }>,
  formData: CreateOrderFormData,
) {
  try {
    localStorage.setItem(PR_DRAFT_CART_KEY, JSON.stringify(cart));
    localStorage.setItem(PR_DRAFT_FORM_KEY, JSON.stringify(formData));
  } catch {
    // localStorage may be full or unavailable
  }
}

/** Remove PR draft from localStorage. */
export function clearPrDraft() {
  localStorage.removeItem(PR_DRAFT_CART_KEY);
  localStorage.removeItem(PR_DRAFT_FORM_KEY);
}

/** Load PR draft from localStorage. Returns null if nothing saved. */
export function loadPrDraft(): {
  cart: Record<string, { quantity: number; actualPrice: number }>;
  formData: CreateOrderFormData;
} | null {
  try {
    const cartRaw = localStorage.getItem(PR_DRAFT_CART_KEY);
    const formRaw = localStorage.getItem(PR_DRAFT_FORM_KEY);
    if (!cartRaw) return null;
    const cart = JSON.parse(cartRaw) as Record<string, { quantity: number; actualPrice: number }>;
    const formData = formRaw
      ? (JSON.parse(formRaw) as CreateOrderFormData)
      : undefined;
    if (!formData || Object.keys(cart).length === 0) return null;
    return { cart, formData };
  } catch {
    return null;
  }
}

export interface PurchaseRequisitionCatalogItem {
  id: string;
  name: string;
  nsn?: string | null;
  lin?: string | null;
  unitPrice?: number | null;
  entity_data?: Record<string, unknown>;
  size?: string | null;
}

export interface PurchaseRequisitionCartItem {
  catalogId: string;
  quantity: number;
  actualPrice?: number;
}

export interface CreateOrderFormData {
  purchaseType: string;
  description: string;
  ufr: boolean;
  logFormNumber: string;
  assetAuthority: string;
  priority: string;
  vendor: string;
  expectedDeliveryDate: string;
  requiredByDate: string;
  fmz: string;
  wbsCc: string;
}

export interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (items: PurchaseRequisitionCartItem[], formData: CreateOrderFormData) => Promise<void>;
  catalogItems: PurchaseRequisitionCatalogItem[];
  isLoadingCatalog?: boolean;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

export const CreateOrderModal = ({
  open,
  onClose,
  onSubmit,
  catalogItems,
  isLoadingCatalog = false,
  isSubmitting = false,
  errorMessage,
}: CreateOrderModalProps) => {
  const { config: supplyConfig } = useModuleConfig<SupplyModuleConfig>('supply');
  const supplyLabels = supplyConfig.features.purchase_requisitions;
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<Record<string, { quantity: number; actualPrice: number }>>({});
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ purchaseType?: string; description?: string; logFormNumber?: string }>({});
  const [formData, setFormData] = useState<CreateOrderFormData>({
    purchaseType: '',
    description: '',
    ufr: false,
    logFormNumber: '',
    assetAuthority: supplyLabels.asset_authority_default,
    priority: '',
    vendor: '',
    expectedDeliveryDate: '',
    requiredByDate: '',
    fmz: '',
    wbsCc: '',
  });
  const [priceErrors, setPriceErrors] = useState<Record<string, string>>({});
  const submitLockRef = useRef(false);

  const [catalogPage, setCatalogPage] = useState(0);

  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [isForceClosing, setIsForceClosing] = useState(false);

  // Row-level drafts for actual price and quantity before adding to cart
  const [rowDrafts, setRowDrafts] = useState<
    Record<string, { actualPrice: string; quantity: number }>
  >({});

  // Sync asset-authority default when config loads asynchronously
  useEffect(() => {
    setFormData((prev) => {
      if (prev.assetAuthority === '' || prev.assetAuthority === supplyLabels.asset_authority_default) {
        return { ...prev, assetAuthority: supplyLabels.asset_authority_default };
      }
      return prev;
    });
  }, [supplyLabels.asset_authority_default]);

  // Restore draft from localStorage when modal opens
  useEffect(() => {
    if (open) {
      const draft = loadPrDraft();
      if (draft) {
        setCart(draft.cart);
        setFormData(draft.formData);
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setQtyDrafts({});
      setRowDrafts({});
      setSubmitError(null);
      setFieldErrors({});
      setCatalogPage(0);
      setShowExitPrompt(false);
      setIsForceClosing(false);
    }
  }, [open]);

  useEffect(() => {
    if (errorMessage) {
      setSubmitError(errorMessage);
    }
  }, [errorMessage]);

  const filteredCatalog = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) {
      return catalogItems;
    }
    const tokens = trimmed.split(/\s+/);
    return catalogItems.filter((item) => {
      const name = item.name.toLowerCase();
      const nsn = item.nsn ? item.nsn.toLowerCase() : '';
      const lin = item.lin ? item.lin.toLowerCase() : '';
      return tokens.every((token) => name.includes(token) || nsn.includes(token) || lin.includes(token));
    });
  }, [catalogItems, search]);

  const paginatedCatalog = useMemo(() => {
    const start = catalogPage * CATALOG_ROWS_PER_PAGE;
    return filteredCatalog.slice(start, start + CATALOG_ROWS_PER_PAGE);
  }, [filteredCatalog, catalogPage]);

  const getRowDraft = (catalogId: string, unitPrice: number) => {
    return rowDrafts[catalogId] ?? { actualPrice: unitPrice.toFixed(2), quantity: 1 };
  };

  const handleRowActualPriceChange = (catalogId: string, value: string) => {
    // Clear price error when user types
    setPriceErrors((prev) => {
      const { [catalogId]: _, ...rest } = prev;
      return rest;
    });

    setRowDrafts((previous) => ({
      ...previous,
      [catalogId]: {
        ...getRowDraft(catalogId, 0),
        actualPrice: value,
      },
    }));
  };

  const handleRowQuantityChange = (catalogId: string, delta: number, unitPrice: number) => {
    setRowDrafts((previous) => {
      const current = getRowDraft(catalogId, unitPrice);
      const newQty = Math.max(1, current.quantity + delta);
      return {
        ...previous,
        [catalogId]: {
          ...current,
          quantity: newQty,
        },
      };
    });
  };

  const handleRowQuantitySet = (catalogId: string, value: string, unitPrice: number) => {
    const parsed = Number.parseInt(value, 10);
    const newQty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    setRowDrafts((previous) => ({
      ...previous,
      [catalogId]: {
        ...getRowDraft(catalogId, unitPrice),
        quantity: newQty,
      },
    }));
  };

  const handleAddToCart = (catalogId: string, unitPrice: number) => {
    const draft = getRowDraft(catalogId, unitPrice);
    const parsedPrice = Number.parseFloat(draft.actualPrice);

    // Validate actual price
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setPriceErrors((prev) => ({
        ...prev,
        [catalogId]: 'Please enter a valid price (must be 0 or greater)',
      }));
      return;
    }

    const actualPrice = parsedPrice;

    setCart((previous) => ({
      ...previous,
      [catalogId]: { quantity: draft.quantity, actualPrice },
    }));

    // Reset the row draft after adding
    setRowDrafts((previous) => {
      const { [catalogId]: _, ...rest } = previous;
      return rest;
    });
  };

  const handleRemoveFromCart = (catalogId: string) => {
    setCart((previous) => {
      const { [catalogId]: _, ...rest } = previous;
      return rest;
    });
    setQtyDrafts((previous) => {
      const { [catalogId]: _, ...rest } = previous;
      return rest;
    });
  };

  const handleQtyDraftChange = (catalogId: string, value: string) => {
    setQtyDrafts((previous) => ({ ...previous, [catalogId]: value }));
  };

  const handleQtyCommit = (catalogId: string) => {
    setCart((previous) => {
      const draft = qtyDrafts[catalogId];
      const parsed = Number.parseInt(draft ?? '', 10);
      const currentEntry = previous[catalogId];
      const validValue =
        Number.isFinite(parsed) && parsed > 0 ? parsed : (currentEntry?.quantity ?? 1);
      return {
        ...previous,
        [catalogId]: { ...currentEntry, quantity: validValue } as {
          quantity: number;
          actualPrice: number;
        },
      };
    });
    setQtyDrafts((previous) => {
      const { [catalogId]: _, ...rest } = previous;
      return rest;
    });
  };

  const cartEntries = useMemo(() => Object.entries(cart), [cart]);

  const orderSummary = useMemo(() => {
    const lineItems = cartEntries.length;
    const totalItems = cartEntries.reduce((sum, [_catalogId, cartItem]) => {
      return sum + (cartItem.quantity || 0);
    }, 0);
    const totalCost = cartEntries.reduce((sum, [_catalogId, cartItem]) => {
      return sum + (cartItem.actualPrice * cartItem.quantity || 0);
    }, 0);
    return { lineItems, totalItems, totalCost };
  }, [cartEntries]);

  const lineItemLabel = orderSummary.lineItems === 1 ? 'Line Item' : 'Line Items';
  const totalItemLabel = orderSummary.totalItems === 1 ? 'Total Item' : 'Total Items';

  const handleSubmit = async () => {
    if (submitLockRef.current || isSubmitting) {
      return;
    }

    setSubmitError(null);
    setFieldErrors({});
    const items: PurchaseRequisitionCartItem[] = cartEntries.map(([catalogId, cartItem]) => ({
      catalogId,
      quantity: cartItem.quantity,
      actualPrice: cartItem.actualPrice,
    }));

    // Validation: ensure at least one item
    if (items.length === 0) {
      setSubmitError('Add at least one catalog item before creating the requisition.');
      return;
    }

    // Collect all field-level errors at once
    const errors: { purchaseType?: string; description?: string; logFormNumber?: string } = {};

    if (!formData.purchaseType.trim()) {
      errors.purchaseType = 'Please select a purchase type.';
    }

    if (!formData.description.trim()) {
      errors.description = 'Please enter a description.';
    }

    if (!formData.logFormNumber.trim()) {
      errors.logFormNumber = 'Please enter a log form number.';
    } else if (!/^[0-9]+$/.test(formData.logFormNumber.trim())) {
      errors.logFormNumber = 'Log form number must be a positive integer (no dashes or special characters).';
    } else {
      const logFormNumberValue = Number.parseInt(formData.logFormNumber.trim(), 10);
      if (!Number.isInteger(logFormNumberValue) || logFormNumberValue < 1) {
        errors.logFormNumber = 'Log form number must be a positive integer (no dashes or special characters).';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitError('Please fix the highlighted fields in Requisition Details.');
      return;
    }

    submitLockRef.current = true;

    try {
      await onSubmit(items, formData);
      clearPrDraft();
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Unable to create requisition.');
      }
    } finally {
      submitLockRef.current = false;
    }
  };

  const resetFormState = useCallback(() => {
    setSearch('');
    setCart({});
    setQtyDrafts({});
    setRowDrafts({});
    setSubmitError(null);
    setFieldErrors({});
    setCatalogPage(0);
    setFormData({
      purchaseType: '',
      description: '',
      ufr: false,
      logFormNumber: '',
      assetAuthority: supplyLabels.asset_authority_default,
      priority: '',
      vendor: '',
      expectedDeliveryDate: '',
      requiredByDate: '',
      fmz: '',
      wbsCc: '',
    });
  }, []);

  const forceClose = useCallback(() => {
    setShowExitPrompt(false);
    setIsForceClosing(true);
  }, []);

  const handleClose = useCallback(() => {
    const hasItems = Object.keys(cart).length > 0;
    if (hasItems) {
      setShowExitPrompt(true);
    } else {
      clearPrDraft();
      resetFormState();
      onClose();
    }
  }, [cart, resetFormState, onClose]);

  const handleSave = useCallback(() => {
    savePrDraft(cart, formData);
    resetFormState();
    forceClose();
  }, [cart, formData, resetFormState, forceClose]);

  const handleCancelRequisition = useCallback(() => {
    clearPrDraft();
    resetFormState();
    forceClose();
  }, [resetFormState, forceClose]);

  const plannerToolbar = (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search catalog…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setCatalogPage(0);
        }}
        sx={{ maxWidth: 420 }}
      />
    </Stack>
  );

  const plannerContent = (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small" aria-label="Catalog items">
          <TableHead>
            <TableRow>
              <TableCell>LIN</TableCell>
              <TableCell>NSN</TableCell>
              <TableCell>Nomenclature</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Actual Price</TableCell>
              <TableCell align="center">Quantity</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingCatalog ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredCatalog.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No catalog items found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCatalog.map((catalogItem) => {
                const isInCart = Boolean(cart[catalogItem.id]);
                const unitPrice = catalogItem.unitPrice ?? 0;
                const rowDraft = getRowDraft(catalogItem.id, unitPrice);
                return (
                  <TableRow key={catalogItem.id} hover>
                    <TableCell>{(catalogItem.entity_data?.lin as string) || '—'}</TableCell>
                    <TableCell>{catalogItem.nsn || '—'}</TableCell>
                    <TableCell>{formatNomenclatureWithSize(catalogItem.name, catalogItem.size)}</TableCell>
                    <TableCell align="right">${unitPrice.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={rowDraft.actualPrice}
                        onChange={(e) => handleRowActualPriceChange(catalogItem.id, e.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                        sx={{ width: 100 }}
                        disabled={isInCart}
                        error={Boolean(priceErrors[catalogItem.id])}
                        helperText={priceErrors[catalogItem.id]}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleRowQuantityChange(catalogItem.id, -1, unitPrice)}
                          disabled={isInCart || rowDraft.quantity <= 1}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={rowDraft.quantity}
                          onChange={(e) =>
                            handleRowQuantitySet(catalogItem.id, e.target.value, unitPrice)
                          }
                          slotProps={{ htmlInput: { min: 1 } }}
                          sx={{ width: 70 }}
                          disabled={isInCart}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRowQuantityChange(catalogItem.id, 1, unitPrice)}
                          disabled={isInCart}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant={isInCart ? 'outlined' : 'contained'}
                        color={isInCart ? 'secondary' : 'primary'}
                        size="small"
                        endIcon={!isInCart ? <AddIcon /> : undefined}
                        onClick={() =>
                          isInCart
                            ? handleRemoveFromCart(catalogItem.id)
                            : handleAddToCart(catalogItem.id, unitPrice)
                        }
                      >
                        {isInCart ? 'Remove' : 'Add'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredCatalog.length}
        page={catalogPage}
        onPageChange={(_event, newPage) => setCatalogPage(newPage)}
        rowsPerPage={CATALOG_ROWS_PER_PAGE}
        rowsPerPageOptions={[CATALOG_ROWS_PER_PAGE]}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </Paper>
  );

  const sidebarContent = (
    <Stack spacing={3}>
      {submitError ? (
        <Box sx={{ p: 2, borderRadius: 1, bgcolor: (theme) => theme.palette.error.light }}>
          <Typography variant="body2" color="error.contrastText">
            {submitError}
          </Typography>
        </Box>
      ) : null}
      {/* Requisition Details Form */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Requisition Details
        </Typography>
        <Stack spacing={2}>
          <FormControl size="small" fullWidth required error={!!fieldErrors.purchaseType}>
            <InputLabel>Purchase Type</InputLabel>
            <Select
              value={formData.purchaseType}
              label="Purchase Type"
              onChange={(e) => {
                setFormData({ ...formData, purchaseType: e.target.value });
                setFieldErrors((prev) => { const { purchaseType: _, ...rest } = prev; return rest; });
              }}
            >
              {PURCHASE_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.purchaseType ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldErrors.purchaseType}
              </Typography>
            ) : null}
          </FormControl>
          <TextField
            size="small"
            fullWidth
            required
            label="Description"
            value={formData.description}
            onChange={(e) => {
              setFormData({ ...formData, description: e.target.value });
              setFieldErrors((prev) => { const { description: _, ...rest } = prev; return rest; });
            }}
            error={!!fieldErrors.description}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            helperText={fieldErrors.description ?? `${formData.description.length}/500 characters`}
          />
          <TextField
            size="small"
            fullWidth
            required
            label="Log Form #"
            value={formData.logFormNumber}
            onChange={(e) => {
              const value = e.target.value;
              setFormData({ ...formData, logFormNumber: value });
              setFieldErrors((prev) => { const { logFormNumber: _, ...rest } = prev; return rest; });
              setSubmitError(null);
            }}
            error={!!fieldErrors.logFormNumber}
            helperText={fieldErrors.logFormNumber}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label={supplyLabels.asset_authority_label}
            value={formData.assetAuthority}
            onChange={(e) => setFormData({ ...formData, assetAuthority: e.target.value })}
            helperText={supplyLabels.asset_authority_helper}
          />
          <TextField
            size="small"
            fullWidth
            label="Vendor"
            value={formData.vendor}
            onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            label="Expected Delivery Date"
            type="date"
            value={formData.expectedDeliveryDate}
            onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            fullWidth
            label="Required By Date"
            type="date"
            value={formData.requiredByDate}
            onChange={(e) => setFormData({ ...formData, requiredByDate: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            fullWidth
            label="FMZ"
            value={formData.fmz}
            onChange={(e) => setFormData({ ...formData, fmz: e.target.value })}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>WBS / CC</InputLabel>
            <Select
              value={formData.wbsCc}
              label="WBS / CC"
              onChange={(e) => setFormData({ ...formData, wbsCc: e.target.value })}
            >
              {WBS_CC_OPTIONS.map((w) => (
                <MenuItem key={w} value={w}>
                  {w}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ufr}
                onChange={(e) => setFormData({ ...formData, ufr: e.target.checked })}
                size="small"
              />
            }
            label="UFR (Unfunded Requirement)"
          />
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Requisition Items
        </Typography>
        <Stack spacing={1.5}>
          {cartEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No items added yet
            </Typography>
          ) : (
            cartEntries.map(([catalogId, cartItem]) => {
              const catalogItem = catalogItems.find((item) => item.id === catalogId);
              if (!catalogItem) {
                return null;
              }
              const subtitleParts = [];
              if (catalogItem.nsn) {
                subtitleParts.push(`NSN ${catalogItem.nsn}`);
              }
              const subtotal = cartItem.actualPrice * cartItem.quantity;
              subtitleParts.push(`$${cartItem.actualPrice.toFixed(2)} ea`);
              subtitleParts.push(`Subtotal $${subtotal.toFixed(2)}`);

              return (
                <WorkflowCartRow
                  key={catalogId}
                  title={catalogItem.name}
                  subtitle={subtitleParts.join(' • ')}
                  actions={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        value={qtyDrafts[catalogId] ?? cartItem.quantity}
                        slotProps={{ htmlInput: { min: 1 } }}
                        onChange={(event) => handleQtyDraftChange(catalogId, event.target.value)}
                        onBlur={() => handleQtyCommit(catalogId)}
                        sx={{ width: 80 }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label={`Remove ${catalogItem.name} from cart`}
                        onClick={() => handleRemoveFromCart(catalogId)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  }
                />
              );
            })
          )}
        </Stack>
      </Paper>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Requisition Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {orderSummary.lineItems} {lineItemLabel} • {orderSummary.totalItems} {totalItemLabel} • $
          {orderSummary.totalCost.toFixed(2)}
        </Typography>
      </Box>
    </Stack>
  );

  const footer = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
      <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Tooltip
        title={cartEntries.length === 0 ? 'Add at least one catalog item' : ''}
        disableHoverListener={cartEntries.length > 0}
      >
        <span>
          <ViewerGuardedButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={cartEntries.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Creating…' : 'Create Requisition'}
          </ViewerGuardedButton>
        </span>
      </Tooltip>
    </Box>
  );

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={handleClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Purchase Requisitions
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={handleClose}
        disableFocusTrap={true}
        title="Create Requisition"
        breadcrumbs={
          <Breadcrumbs aria-label="Breadcrumb">
            <MUILink
              underline="hover"
              color="inherit"
              href="#"
              onClick={(event) => event.preventDefault()}
            >
              Supply
            </MUILink>
            <Typography color="text.primary">Purchase Requisitions</Typography>
          </Breadcrumbs>
        }
        plannerTitle="Catalog"
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle="Requisition Cart"
        sidebarContent={sidebarContent}
        footer={footer}
        closeButton={closeButton}
      />

      <Dialog
        open={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        TransitionProps={{
          onExited: () => {
            if (isForceClosing) {
              onClose();
              setIsForceClosing(false);
            }
          },
        }}
        aria-labelledby="pr-exit-title"
        aria-describedby="pr-exit-description"
      >
        <DialogTitle id="pr-exit-title">Items in Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="pr-exit-description">
            You have items in your requisition cart. You can save your progress for later or cancel
            to discard the draft.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRequisition} color="error">
            Cancel Requisition
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowExitPrompt(false)} color="inherit">
            Back to Cart
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
